"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import NextImage from "@components/Image/NextImage";
import PrivacyCookiePopup from "@components/Header/PrivacyCookiePopup";
import { useAppContext } from "@components/Provider";
import EBBLandingSignupForm from "./EBBLandingSignupForm";
import styles from "./EBBLandingPage.module.css";

const Modal = dynamic(() => import("@components/Modal/Modal"), { ssr: false });
const Login = dynamic(() => import("@components/Login/Login"), { ssr: false });
const ForgotPassword = dynamic(() => import("@components/ForgotPassword/ForgotPassword"), {
  ssr: false,
});

export const renderAccentText = (text, accentClassName) => {
  if (!text) return null;
  return String(text)
    .split(/(\*[^*]+\*)/g)
    .map((part, index) => {
      const match = part.match(/^\*([^*]+)\*$/);
      if (match) {
        return (
          <span key={index} className={accentClassName || styles.accent}>
            {match[1]}
          </span>
        );
      }
      return part;
    });
};

export const ArtImage = ({ desktopSrc, mobileSrc, className, alt = "", fetchPriority }) => {
  if (!desktopSrc && !mobileSrc) return null;
  if (desktopSrc && mobileSrc && desktopSrc !== mobileSrc) {
    return (
      <picture>
        <source media="(min-width: 768px)" srcSet={desktopSrc} />
        <img src={mobileSrc} alt={alt} className={className} fetchPriority={fetchPriority} />
      </picture>
    );
  }
  return (
    <img
      src={desktopSrc || mobileSrc}
      alt={alt}
      className={className}
      fetchPriority={fetchPriority}
    />
  );
};

const StarRow = () => (
  <div className={styles.stars} aria-hidden="true">
    {Array.from({ length: 5 }).map((_, index) => (
      <span key={index}>★</span>
    ))}
  </div>
);

export const Testimonials = ({ quotes, classes = {} }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (quotes.length < 2) {
      return undefined;
    }
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setPage((current) => (current + 1) % quotes.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [quotes.length]);

  return (
    <section className={classes.section || styles.testimonials}>
      <div className={classes.desktop || styles.testimonialsDesktop}>
        {quotes.map((quote, index) => (
          <div className={classes.card || styles.testimonialCard} key={`quote-desktop-${index}`}>
            <StarRow />
            <p>{quote.text}</p>
          </div>
        ))}
      </div>
      <div className={classes.mobile || styles.testimonialsMobile}>
        <div className={classes.card || styles.testimonialCard}>
          <StarRow />
          <p>{quotes[page]?.text}</p>
        </div>
        <div className={styles.dots} role="tablist" aria-label="Reviews">
          {quotes.map((_, index) => (
            <button
              key={`quote-dot-${index}`}
              type="button"
              className={`${styles.dot} ${page === index ? styles.dotActive : ""}`}
              aria-label={`Show review ${index + 1}`}
              onClick={() => setPage(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const LandingSignupModal = ({
  isOpen,
  onClose,
  formId,
  headline,
  signUpLocation,
  newsletterProps,
  Newsletter_Settings,
  postType,
}) => (
  <Modal
    isOpen={isOpen}
    closeBtn={onClose}
    sizeClass="small"
    className={styles.signupModalShell}
    transitionName="modal-anim"
  >
    {headline ? <h2 className={styles.signupModalHeadline}>{headline}</h2> : null}
    <EBBLandingSignupForm
      variant="modal"
      formId={formId}
      signUpLocation={signUpLocation}
      newsletterProps={newsletterProps}
      Newsletter_Settings={Newsletter_Settings}
      postType={postType}
    />
  </Modal>
);

export const LandingHeader = ({
  siteLogo,
  siteName,
  showStickySignup,
  onCtaClick,
  headline,
  ctaText,
  signUpLocation,
  pageNewsletter,
  Newsletter_Settings,
  postType,
  stickyFormId = "landing-sticky-form",
  solidHeader = false,
  headlineAccentClass,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [privacyCookie, setPrivacyCookie] = useState("hidden");
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const { siteId, data, tokenCookie } = useAppContext();

  useEffect(() => {
    if (localStorage.getItem("cookiePopup")) setPrivacyCookie("shown");
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {privacyCookie !== "shown" && (
        <PrivacyCookiePopup setPrivacyCookie={setPrivacyCookie} siteId={siteId} data={data} />
      )}
      <header
        className={`${styles.header} ${solidHeader ? styles.headerSolid : ""} ${
          scrolled || showStickySignup ? styles.headerScrolled : ""
        } ${showStickySignup ? styles.headerSticky : ""}`}
        data-ebb-landing-header="true"
      >
        <div className={styles.headerRow} data-ebb-landing-header-row="true">
          <a href="/" className={styles.logoWrap} aria-label="Early Bird Books home">
            {siteLogo ? (
              <NextImage
                ImageUrl={siteLogo}
                Title={siteName || "Early Bird Books"}
                Alt_Text={siteName || "Early Bird Books"}
                className={styles.logoImage}
                width={220}
                height={40}
                isLazyload={false}
                fetchpriority={true}
              />
            ) : (
              <span className={styles.logoFallback}>EARLY BIRD BOOKS</span>
            )}
          </a>
          {!tokenCookie ? (
            <button type="button" className={styles.loginBtn} onClick={() => setIsLoginOpen(true)}>
              LOG IN
            </button>
          ) : (
            <Link href="/account/profile" className={styles.loginBtn}>
              ACCOUNT
            </Link>
          )}
        </div>
        <div
          className={`${styles.stickySignup} ${showStickySignup ? styles.stickySignupOpen : ""}`}
          aria-hidden={!showStickySignup}
          inert={showStickySignup ? undefined : true}
        >
          <div className={styles.stickySignupInner}>
            {headline ? (
              <p className={styles.stickyHeadline}>
                {renderAccentText(String(headline).replace(/\n+/g, " "), headlineAccentClass)}
              </p>
            ) : null}
            <div className={styles.stickyDesktopForm}>
              <EBBLandingSignupForm
                variant="sticky"
                formId={stickyFormId}
                signUpLocation={signUpLocation}
                newsletterProps={pageNewsletter}
                Newsletter_Settings={Newsletter_Settings}
                postType={postType}
              />
            </div>
            {ctaText ? (
              <button
                type="button"
                className={styles.stickyMobileCta}
                onClick={onCtaClick}
                tabIndex={showStickySignup ? 0 : -1}
              >
                {ctaText}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      <Modal isOpen={isLoginOpen} closeBtn={() => setIsLoginOpen(false)} className="LoginModal">
        <Login
          openForgotPasswordModal={() => {
            setIsLoginOpen(false);
            setIsForgotPasswordOpen(true);
          }}
          closeLoginModal={() => setIsLoginOpen(false)}
          buttonText="LOGIN"
          classNames="button"
          hidePlaceHolder={true}
        />
      </Modal>
      <Modal
        isOpen={isForgotPasswordOpen}
        closeBtn={() => setIsForgotPasswordOpen(false)}
        className="ForgotPassword"
      >
        <ForgotPassword />
      </Modal>
      <style jsx global>{`
        .LoginModal .medium-modal.modal {
          width: 45%;
          padding: 2rem;
          border-radius: 10px;
        }
        @media (max-width: 1024px) {
          .LoginModal .medium-modal.modal {
            width: 90%;
          }
        }
      `}</style>
    </>
  );
};
